import React, { useState, useRef } from 'react';
import axiosInstance from '../../axiosInstance';
import CustomSelect from './CustomSelect';
import ProjectSelect from './ProjectSelect';

const API_BASE = '/redmine-portal';

function CreateIssue({ projects, trackers, priorities, onSubmit }) {
  const [projectId, setProjectId] = useState('');
  const [trackerId, setTrackerId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    const newAttachments = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await axiosInstance.post(`${API_BASE}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        newAttachments.push({
          token: res.data.token,
          filename: res.data.filename,
          content_type: file.type
        });
      } catch (err) {
        console.error('Upload error:', err);
        setError(`Failed to upload ${file.name}`);
      }
    }

    setAttachments([...attachments, ...newAttachments]);
    setUploading(false);
    e.target.value = '';
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectId) {
      setError('Please select a project');
      return;
    }
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }

    setSubmitting(true);
    setError(null);

    const issueData = {
      project_id: projectId,
      subject: subject.trim(),
      description: description.trim()
    };

    if (trackerId) issueData.tracker_id = trackerId;
    if (priorityId) issueData.priority_id = priorityId;
    if (attachments.length > 0) {
      issueData.uploads = attachments;
    }

    const result = await onSubmit(issueData);

    if (result.success) {
      setSuccess(`Issue #${result.issue.id} created successfully!`);
      // Reset form
      setProjectId('');
      setTrackerId('');
      setSubject('');
      setDescription('');
      setPriorityId('');
      setAttachments([]);
    } else {
      setError(result.error);
    }

    setSubmitting(false);
  };

  return (
    <div className="create-issue">
      <h2>Create New Issue</h2>

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Project *</label>
            <ProjectSelect
              projects={projects}
              value={projectId}
              onChange={setProjectId}
              placeholder="Select project..."
            />
          </div>

          <div className="form-group">
            <label>Tracker</label>
            <CustomSelect
              value={trackerId}
              onChange={setTrackerId}
              placeholder="Default"
              className="full-width"
              options={[
                { value: '', label: 'Default' },
                ...trackers.map(t => ({ value: String(t.id), label: t.name }))
              ]}
            />
          </div>

          <div className="form-group">
            <label>Priority</label>
            <CustomSelect
              value={priorityId}
              onChange={setPriorityId}
              placeholder="Default"
              className="full-width"
              options={[
                { value: '', label: 'Default' },
                ...priorities.map(p => ({ value: String(p.id), label: p.name }))
              ]}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Subject *</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter issue subject..."
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue..."
            rows={8}
          />
        </div>

        {/* Attachments */}
        <div className="form-group">
          <label>Attachments</label>
          <div className="attachments-upload">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn-upload"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Add Files'}
            </button>

            {attachments.length > 0 && (
              <div className="attachment-list">
                {attachments.map((att, i) => (
                  <div key={i} className="attachment-item">
                    <span>{att.filename}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="btn-remove"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {error && <p className="form-error">{error}</p>}

        <div className="form-actions">
          <button
            type="submit"
            className="btn-create"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Issue'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateIssue;
